import { z } from 'zod';
import { paginationSchema, paginationResponseSchema, sortOrderSchema, } from './helpers';
import { userSchema } from './user';
import { severitySchema } from './shared/severity';
import { extensionCapabilitySchema, scoreContributorSchema } from './analyses';
/*
  scanIdSchema validates a scan ID parameter.
  It must be a valid UUID.
*/
export const scanIdSchema = z.uuid('Scan ID must be a valid UUID');
/*
  organizationScanListSortFieldSchema defines the sortable fields for
  organization scan history endpoints.
*/
export const organizationScanListSortFieldSchema = z.enum([
    'score',
    'createdAt',
]);
/*
  organizationScanListQuerySchema extends paginationSchema with optional
  sortBy and sortOrder fields used by organization scan list endpoints.
*/
export const organizationScanListQuerySchema = paginationSchema.extend({
    sortBy: organizationScanListSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
/*
  issueFixSchema defines the structure of a fix for a security issue.
*/
export const settingFixSchema = z.object({
    type: z.literal('setting'),
    path: z.string(),
    safeValue: z.unknown(),
    isDefault: z.boolean(),
});
export const fileFixSchema = z.object({
    type: z.literal('quarantine'),
});
export const issueFixSchema = z.discriminatedUnion('type', [
    settingFixSchema,
    fileFixSchema,
]);
/*
  securityIssueSchema defines the structure of a security issue found in a scan.

  `subPolicyKey` (KIR-2378) is the canonical sub-policy identifier the backend
  persists on `findings.sub_policy_key`. Scanners may set it to disambiguate
  shared finding `type` values (e.g. `blocked_by_policy` is emitted by both
  `extensions-management` and `mcp-server-sources`); when omitted, the backend
  registry resolves it from `(scannerKey, type)`. The full set of legal values
  lives in `apps/backend/src/policies/alert-mapping.ts`.

  Optional in this contract because clients (IDE hooks) typically do not set
  it and older backends don't read it. The backend always derives the parent
  `policyKey` from this value at the DTO boundary, so consumers of grouped
  alerts never need to look up the parent policy themselves.
*/
/*
  allowListContextSchema (KIR-3073, PR4) carries the raw allow/block-list match
  candidates the agent assembled when scanning. The `/api/v2/agent/decisions`
  PDP route strips client-supplied `allowed` (security: a key holder cannot
  claim allow-list status) and re-derives it server-side. To do that without
  re-running scanners on the full source config (which is not in the request
  body), the agent ships the same candidate shapes its scanner would have
  matched locally:
    - `extension` for `extensions-management` (id + publisher + version)
    - `mcp.candidates` for `mcp-server-sources` (one entry per git remote /
      inventory / package candidate the agent assembled)
  Both fields are optional and ignored by every non-PDP consumer.
*/
const allowListExtensionCandidateSchema = z
    .object({
    extensionId: z.string(),
    publisher: z.string(),
    version: z.string(),
})
    .strict();
const allowListMcpCandidateSchema = z
    .object({
    name: z.string(),
    publisher: z.string().optional(),
    type: z.string().optional(),
})
    .strict();
export const allowListContextSchema = z
    .object({
    extension: allowListExtensionCandidateSchema.optional(),
    mcp: z
        .object({
        // Empty candidates is a no-op for the resolver; reject at the edge
        // so the client doesn't ship a useless context that masks a bug.
        candidates: z.array(allowListMcpCandidateSchema).min(1),
    })
        .strict()
        .optional(),
})
    .strict();
export const securityIssueSchema = z.object({
    type: z.string(),
    severity: severitySchema,
    description: z.string(),
    location: z.string().optional(),
    fix: issueFixSchema.optional(),
    rawConfigName: z.string().optional(),
    allowed: z.boolean().optional(),
    subPolicyKey: z.string().optional(),
    /*
      KIR-3267: the backend's stable, config-independent target identity for this
      finding (the normalized location behind `finding_key` — file path,
      `editor:extId` for extensions, or server for MCP). Clients use it as the
      dedup-key target for the 5h repeat-alert gate. Optional + additive: older
      backends omit it and clients fall back to the scan's file path.
    */
    dedupTarget: z.string().optional(),
    allowListContext: allowListContextSchema.optional(),
    /*
      KIR-3765 secret-detection enrichment. All optional + additive so older
      clients/backends are unaffected. `engine` is the detection engine
      ("gitleaks" | "regex" | "file-pattern"); `ruleId` the matched rule;
      `entropy` is present ONLY for gitleaks (omitted for regex/file-pattern, so
      it is never a misleading 0). `toolName`/`sessionId` are per-event context.
      `workspaceName` is the full workspace/working-directory path where the
      secret surfaced (the earlier basename-only trim was dropped).
      These ride onto `findings.custom_attributes` via the `secret-leakage`
      custom-attribute shape and surface on alert occurrences (flag-gated).
    */
    engine: z.string().optional(),
    ruleId: z.string().optional(),
    /*
      KIR-3765: human-readable rule name for Kirin (regex / file-pattern) rules,
      e.g. "AWS Access Key ID" — `ruleId` for those engines is the raw regex
      pattern, which is unreadable. Omitted for gitleaks (its `ruleId` is already
      a readable rule id like "aws-access-token").
    */
    ruleName: z.string().optional(),
    entropy: z.number().optional(),
    toolName: z.string().optional(),
    sessionId: z.string().optional(),
    workspaceName: z.string().optional(),
    /*
      KIR-3765: first-class tool-event side. `pre` = tool INPUT (pre-execution),
      `post` = tool OUTPUT (post-execution). The backend derives this at ingest
      from the discrete hook event and persists it onto `custom_attributes`, so the
      input-vs-output distinction no longer depends on parsing the `file_path`
      sentinel. Absent for non-tool events. Flag-gated on the alerts read path.
    */
    eventKind: z.enum(['pre', 'post']).optional(),
    /*
      KIR-3765: backend-built, doubly-redacted "Context" snippet (~50 chars either
      side of the secret; secret + surrounding PII masked). Set by the backend at
      ingest — a client-supplied value is ignored/overwritten (like `eventKind`) —
      and persisted onto `findings.custom_attributes`. Best-effort: reflects the
      earliest matching occurrence when a secret repeats. Raw content is never
      stored. Exposure is flag-gated behind `secret_detection_details`.
    */
    matchContext: z.string().optional(),
    /**
     * KIR-3781: catalog aggregate score (not the severity→0–100 alert `score`).
     * Round-tripped via `findings.custom_attributes` for extension-security.
     */
    score: z.number().int().min(0).optional(),
    /**
     * KIR-3781: weighted contributors persisted on `custom_attributes`.
     */
    scoreContributors: z.array(scoreContributorSchema).optional(),
    /**
     * KIR-3916: the client ACTUALLY blocked this specific finding (enforce mode and
     * the finding cleared every prevention gate). Structured, per-issue successor to
     * the two mechanisms it replaces:
     *
     *   1. the description-keyword heuristic in `deriveOutcome`
     *      (`apps/backend/src/domain/findings/finding-identity.ts`), which the IDE
     *      extensions fed by rewriting "detected" → "prevented" in the finding
     *      description — the coupling that file's own comment marked as temporary
     *      "until a structured `preventedByClient` boolean lands on the
     *      SecurityIssue contract";
     *   2. the scan-level `extraData.enforcementPrevented` flag (still sent by the
     *      Go agent, still honored as a fallback), which cannot distinguish a
     *      blocked finding from a sub-threshold one reported in the same event.
     *
     * Per-issue is the point: a mixed event blocks only the findings clearing the
     * severity + confidence gates, so only those are `prevented`; the rest stay
     * `detected` at their real severity. Optional + additive — absent means "fall
     * back to the scan-level flag", not `false`.
     *
     * Severity is deliberately NOT changed by prevention (KIR-3916). Severity says
     * what was found; `prevented` / outcome says what happened to it.
     */
    prevented: z.boolean().optional(),
});
/*
  scanResultSchema defines the structure of scan results.
*/
export const scanResultSchema = z.object({
    riskLevel: severitySchema,
    issues: z.array(securityIssueSchema),
    score: z.number().int().min(0).max(100),
    capabilities: z.array(extensionCapabilitySchema).optional(),
});
/*
  scanRequestSchema defines the structure of a scan request record.
*/
export const scanRequestSchema = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    apiKeyId: z.uuid(),
    organizationId: z.string().nullable(),
    filePath: z.string(),
    fileType: z.string(),
    riskLevel: severitySchema,
    issuesCount: z.number().int().min(0),
    score: z.number().int().min(0).max(100),
    clientVersion: z.string().nullable(),
    fileRoot: z.string().nullable().optional(),
    relativePath: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    // User information (optional, only included for organization scans)
    user: userSchema.optional(),
});
/*
  scanDetailsSchema defines the complete scan details including results.
*/
export const scanDetailsSchema = z.object({
    scan: scanRequestSchema,
    results: scanResultSchema.nullable(),
});
/*
  scanHistorySchema defines the structure for scan history responses.
*/
export const scanHistorySchema = z.object({
    history: z.array(scanRequestSchema),
    pagination: paginationResponseSchema,
});
/*
  scanUserUsageSchema defines the structure for a user associated with scans
  matching a particular alert key (currently file type) within an organization.
  It includes basic user information plus usage metadata for UI drill-down.
*/
export const scanUserUsageSchema = z.object({
    user: userSchema,
    scanCount: z.number().int().min(1),
    lastSeenAt: z.string().datetime(),
});
/*
  scanUsersResponseSchema defines the response structure for endpoints that
  return users associated with a specific alert within an organization's scans.
*/
export const scanUsersResponseSchema = z.object({
    // Composite `<subPolicyKey>::<type>` title key, echoed back from the
    // request path. Matches `organizationDashboardTopAlertSchema.alertName`.
    alertName: z.string(),
    users: z.array(scanUserUsageSchema),
});
/*
  scanLogsSchema defines the response structure for scan logs endpoints.
  It returns the scan request object (without user info) and its results with issues.
  Uses strict() to reject any user field that might be present.
*/
export const scanLogsSchema = z.object({
    scan: scanRequestSchema.omit({ user: true }).strict(),
    results: scanResultSchema.nullable(),
});
// Re-export the risk level schema used by scans so that other
// contracts (e.g. organization-dashboard) can depend on a
// single source of truth for risk levels.
export const riskLevelSchema = severitySchema;
//# sourceMappingURL=scans.js.map